import { useEffect, useReducer } from "react";

// Язык категорий Kleinanzeigen и их параметров — только для показа в панели.
//
// Kleinanzeigen отдаёт рубрики и параметры по-немецки, и в Kleinanzeigen они
// уходят как есть: номер категории и коды значений («sehr_gut», «ja»), а не
// подписи. Поэтому перевод здесь касается одних подписей на экране — catText
// вызывается там, где название показывается, а не там, где оно хранится или
// отправляется. По умолчанию — немецкий, как было.
//
// Выбор хранится в браузере, как и язык интерфейса. Словарь (categoryNames.js)
// грузится отдельным файлом и только когда выбран не немецкий: пока он в
// пути, видны немецкие названия, а по приходу экран перерисовывается.
const STORAGE_KEY = "umbrella_category_lang";

export const CATEGORY_LANGUAGES = [
  { value: "de", label: "Deutsch" },
  { value: "ru", label: "Русский" },
  { value: "en", label: "English" }
];
const SUPPORTED = CATEGORY_LANGUAGES.map((item) => item.value);
const DEFAULT_LANG = "de";
const COLUMN = { ru: 0, en: 1 };

const readStoredLang = () => {
  try {
    const raw = String(window.localStorage.getItem(STORAGE_KEY) || "").trim();
    return SUPPORTED.includes(raw) ? raw : DEFAULT_LANG;
  } catch (error) {
    return DEFAULT_LANG;
  }
};

let currentLang = typeof window === "undefined" ? DEFAULT_LANG : readStoredLang();
let dictionary = null;
// Тот же словарь без учёта регистра: черновик хранит код значения, и из
// «sehr_gut» получается «Sehr gut», а в словаре — «Sehr Gut».
let dictionaryLower = null;
let dictionaryLoading = null;
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

const ensureDictionary = () => {
  if (currentLang === DEFAULT_LANG || dictionary || dictionaryLoading) return;
  dictionaryLoading = import("./categoryNames")
    .then((module) => {
      dictionary = module.default || {};
      dictionaryLower = new Map(Object.entries(dictionary).map(([key, value]) => [key.toLowerCase(), value]));
      notify();
    })
    .catch(() => {
      // Не догрузился (сеть, новая сборка) — остаётся немецкий; попробуем
      // снова при следующей смене языка.
      dictionaryLoading = null;
    });
};
ensureDictionary();

export const getCategoryLang = () => currentLang;

export const setCategoryLang = (lang) => {
  const next = SUPPORTED.includes(lang) ? lang : DEFAULT_LANG;
  if (next === currentLang) return;
  currentLang = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch (error) {
    // Не сохранилось — выбор доживёт до перезагрузки страницы.
  }
  ensureDictionary();
  notify();
};

// Подписка на смену языка категорий и на приход словаря. Возвращает отметку,
// которая меняется в обоих случаях, — её удобно класть в зависимости useMemo.
export const useCategoryLang = () => {
  const [, force] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => listeners.delete(force);
  }, []);
  return `${currentLang}${dictionary ? "" : ":pending"}`;
};

// Подпись категории или параметра на выбранном языке. Чего нет в словаре
// (марки, модели, размеры, новые рубрики) — как есть.
export const catText = (value) => {
  const text = String(value ?? "");
  if (currentLang === DEFAULT_LANG || !dictionary) return text;
  const key = text.trim();
  if (!key) return text;
  const entry = dictionary[key] || dictionaryLower.get(key.toLowerCase());
  return entry?.[COLUMN[currentLang]] || text;
};
